/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import {
    AnimatedStateContext,
    Button,
    LoadingIndicator,
    Overlay,
    OverlayCloseButton,
    OverlayRoot,
    Portal,
    useOverlayClose,
} from '@gitbook/spine-react';

import { useKeyboardShortcuts } from '../../Keyboard';
import { getImageResizedURL } from '../../utils';
import { useDrawingSVGElement } from '../ContentEditor/drawing/data';
import { DrawingGenericBlankslate } from '../ContentEditor/drawing/DrawingGenericBlankslate';
import { type MediaGalleryItem, mediaGalleryAtom, mediaGalleryDefaultState } from './state';
/**
 * Overlay portal to show images zoomed.
 */
export function MediaGallery() {
    const [state, setState] = useAtom(mediaGalleryAtom);

    return (
        <OverlayRoot
            isOpen={state.medias.length > 0}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    setState(mediaGalleryDefaultState);
                }
            }}
        >
            <MediaGalleryModal>
                <MediaGalleryContent medias={state.medias} initialIndex={state.initialIndex} />
            </MediaGalleryModal>
        </OverlayRoot>
    );
}

function MediaGalleryModal(props: { children: React.ReactNode }) {
    const animatedState = React.useContext(AnimatedStateContext);

    if (animatedState === 'exited') {
        return null;
    }

    return (
        <Portal>
            <Overlay
                className={clsx(
                    'fixed inset-0 z-overlay flex items-center justify-center bg-overlay/90 backdrop-blur-2xl hover:cursor-zoom-out',
                    'transition [transition-delay:30ms] [transition-duration:150ms] [transition-timing-function:ease-out] fill-mode-forwards',
                    animatedState === 'entering' && 'animate-in fade-in zoom-in-95',
                    animatedState === 'exiting' && 'animate-out fade-out zoom-out-95'
                )}
            >
                {props.children}
            </Overlay>
        </Portal>
    );
}

const SIZES = [480, 768, 1024, 1440, 1920, 2400];

function MediaGalleryContent(props: { medias: MediaGalleryItem[]; initialIndex: number }) {
    const { initialIndex } = props;
    const medias = useLatestMedias(props.medias);
    const closeOverlay = useOverlayClose();
    const [currentIndex, setCurrentIndex] = React.useState(initialIndex);
    const goPrevious = () => setCurrentIndex((prev) => Math.max(prev - 1, 0));
    const goNext = () => setCurrentIndex((prev) => Math.min(prev + 1, medias.length - 1));

    useKeyboardShortcuts({ Left: goPrevious, Right: goNext }, 'all');

    const currentMedia = medias[currentIndex];

    return (
        <div
            className="relative z-10 flex size-full items-center justify-center px-12 pt-4 pb-10"
            onClick={() => {
                closeOverlay();
            }}
        >
            {currentMedia.kind === 'drawing' ? (
                <MediaGalleryDrawing
                    url={currentMedia.url}
                    alt={medias.length > 1 ? `Media #${currentIndex + 1}` : ''}
                />
            ) : (
                <MediaGalleryImage
                    url={currentMedia.url}
                    alt={medias.length > 1 ? `Media #${currentIndex + 1}` : ''}
                />
            )}

            {medias.length > 1 && (
                <span className="text-lg absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1 font-medium text-white tabular-nums">
                    {currentIndex + 1}
                    <span className="opacity-disabled">of</span>
                    {medias.length}
                </span>
            )}
            <OverlayCloseButton
                kind="blank"
                label="Close"
                leadingIcon={Icon.X}
                iconOnly
                className="absolute top-2 right-2"
            />

            {medias.length > 1 && (
                <Button
                    kind="blank"
                    label="Previous"
                    leadingIcon={Icon.ChevronLeft}
                    iconOnly
                    onPress={goPrevious}
                    disabled={currentIndex === 0}
                    className="absolute top-1/2 left-2 z-10 -translate-y-1/2 transform disabled:bg-transparent"
                />
            )}
            {medias.length > 1 && (
                <Button
                    kind="blank"
                    label="Next"
                    leadingIcon={Icon.ChevronRight}
                    iconOnly
                    onPress={goNext}
                    disabled={currentIndex === medias.length - 1}
                    className="absolute top-1/2 right-2 z-10 -translate-y-1/2 transform disabled:bg-transparent"
                />
            )}
        </div>
    );
}

function MediaGalleryImage(props: { url: string; alt?: string }) {
    const { url, alt = '' } = props;
    return (
        <img
            src={url}
            srcSet={SIZES.map(
                (size) => `${getImageResizedURL(url, { width: size })} ${size}w`
            ).join(', ')}
            sizes="100vw"
            alt={alt}
            className="max-h-full max-w-full object-contain"
        />
    );
}

function MediaGalleryDrawing(props: { url: string; alt?: string }) {
    const { url, alt = '' } = props;

    return (
        <Sentry.ErrorBoundary fallback={() => <MediaGalleryDrawingError />}>
            <React.Suspense fallback={<MediaGalleryDrawingLoading />}>
                <MediaGalleryDrawingContent url={url} alt={alt} />
            </React.Suspense>
        </Sentry.ErrorBoundary>
    );
}

function MediaGalleryDrawingContent(props: { url: string; alt?: string }) {
    const { url, alt = '' } = props;
    const svg = useDrawingSVGElement(url, 'dark');
    const svgHTML = React.useMemo(() => {
        const gallerySVG = svg.cloneNode(true) as SVGSVGElement;
        const { width, height } = gallerySVG.viewBox.baseVal;

        if (width > 0) {
            gallerySVG.setAttribute('width', `${width}`);
        }

        if (height > 0) {
            gallerySVG.setAttribute('height', `${height}`);
        }

        return gallerySVG.outerHTML;
    }, [svg]);

    return (
        <div
            className="flex max-h-screen max-w-screen items-center justify-center [&_svg]:max-h-screen [&_svg]:max-w-screen"
            role="img"
            aria-label={alt}
            dangerouslySetInnerHTML={{ __html: svgHTML }}
        />
    );
}

function MediaGalleryDrawingLoading() {
    return (
        <div className="flex max-h-screen max-w-screen items-center justify-center p-6">
            <LoadingIndicator size="xlarge" />
        </div>
    );
}

function MediaGalleryDrawingError() {
    return (
        <div className="flex max-h-screen max-w-screen items-center justify-center p-6">
            <DrawingGenericBlankslate
                kind="danger"
                icon={Icon.Warning}
                message="Unable to preview this drawing."
            />
        </div>
    );
}

/**
 * Preserve the latest medias, so during the closing of the modal, we can still
 * access the latest medias.
 */
function useLatestMedias(medias: MediaGalleryItem[]) {
    const snapshotRef = React.useRef<MediaGalleryItem[]>(medias);
    React.useLayoutEffect(() => {
        if (medias.length > 0) {
            snapshotRef.current = medias;
        }
    }, [medias]);
    return medias.length ? medias : snapshotRef.current;
}
